import { FormControl, InputLabel, MenuItem } from '@mui/material';
import { formatISO, sub } from 'date-fns';
import { useState } from 'react';
import { DetailsDrawer, Option, PageLayout, Pagination, TopBar } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { useSelectedEvent } from 'hooks/useSelectedEvent/useSelectedEvent';
import { AppMessages } from 'i18n/messages';
import { DownloadTableContainer } from 'reusable/downloadTable/DownloadTableContainer';
import { AppRoute } from 'routing/AppRoute.enum';
import { Table } from 'ui/table/Table';

import { AddEventContainer } from './addEvent/AddEventContainer';
import { EventDetailsContainer } from './eventDetails/EventDetailsContainer';
import * as Styled from './Events.styles';
import { EventsProps, RefetchInterval } from './Events.types';
import { EventsFiltersContainer } from './eventsFilters/EventsFiltersContainer';
import { EventsTableHeader } from './eventsTableHeader/EventsTableHeader';
import { EventsTableRow } from './eventsTableRow/EventsTableRow';
import { ExportEventsContainer } from './exportEvents/ExportEventsContainer';

export const Events = ({
  eventsResponse,
  isLoading,
  refetchInterval,
  setRefetchInterval,
  rowsPerPage,
  setRowsPerPage,
  page,
  setPage,
  isHistoryView,
  setTableSearchQuery,
  tableSearchQuery,
  tkdSearchQuery,
  setTkdSearchQuery,
}: EventsProps) => {
  const [archivedSearchSelectedValue, setArchivedSearchSelectedValue] = useState<Option | null>(null);
  const { selectedEvent, setSelectedEvent } = useSelectedEvent();
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();
  const displayFullTable = !selectedEvent;
  const canViewOtherEvents = !!perms?.ZDARZENIA_INNE?.view;
  const canViewPresence = !!perms?.OBECNOSCI?.view;
  const tabsCount = 2 + (canViewOtherEvents ? 1 : 0) + (canViewPresence ? 1 : 0);

  return (
    <PageLayout
      header={formatMessage({ id: AppMessages['events.title'] })}
      renderContent={() => (
        <>
          <Styled.TopBarWrapper gridTemplateColumns={`repeat(${tabsCount}, auto) 1fr auto`}>
            <TopBar.Link linkTo={AppRoute.events} isActive={!isHistoryView}>
              {formatMessage({ id: AppMessages['events.current'] })}
            </TopBar.Link>

            <TopBar.Link linkTo={AppRoute.eventsHistory} isActive={isHistoryView}>
              {formatMessage({ id: AppMessages['events.history'] })}
            </TopBar.Link>

            {canViewOtherEvents && (
              <TopBar.Link linkTo={AppRoute.eventsOther} isActive={false}>
                {formatMessage({ id: AppMessages['events.other'] })}
              </TopBar.Link>
            )}

            {canViewPresence && (
              <TopBar.Link linkTo={AppRoute.eventsCurrentPresence} isActive={false}>
                {formatMessage({ id: AppMessages['events.currentPresence'] })}
              </TopBar.Link>
            )}

            <TopBar.ButtonsWrapper>
              {/* WKD-2471: export button is always shown — system formats (TYP1-4) are
                  always available to select, so it no longer depends on user-defined formats. */}
              <ExportEventsContainer />
              <AddEventContainer />
            </TopBar.ButtonsWrapper>
          </Styled.TopBarWrapper>

          <Table
            isLoading={isLoading}
            gridTemplateColumns={`repeat(${displayFullTable ? 10 : 9}, minmax(100px, 1fr))`}
            data={eventsResponse?.data || []}
            nothingToShowText={formatMessage({ id: AppMessages['events.nothingToShow'] })}
            activeRowId={selectedEvent?.id}
            onClickRow={(event) => setSelectedEvent(selectedEvent?.id === event.id ? undefined : event)}
            renderHeader={() => <EventsTableHeader isHistoryView={isHistoryView} displayFullTable={displayFullTable} />}
            renderRow={(event) => <EventsTableRow event={event} displayFullTable={displayFullTable} />}
            renderAdditionalTopElement={() => (
              <>
                {isHistoryView && (
                  <EventsFiltersContainer
                    archivedSearchSelectedValue={archivedSearchSelectedValue}
                    setArchivedSearchSelectedValue={setArchivedSearchSelectedValue}
                    setTableSearchQuery={setTableSearchQuery}
                    tableSearchQuery={tableSearchQuery}
                    setTkdSearchQuery={setTkdSearchQuery}
                    tkdSearchQuery={tkdSearchQuery}
                  />
                )}

                <Styled.PaginationWrapper>
                  <Pagination
                    page={page}
                    setPage={setPage}
                    count={eventsResponse?.count || 0}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    withoutNavigation={!isHistoryView}
                  />

                  {!isHistoryView && (
                    <>
                      <Styled.DownloadButtonWrapper>
                        <DownloadTableContainer
                          type="events"
                          query={{
                            dataOd: formatISO(sub(new Date(), { days: 1 })),
                            dataDo: formatISO(new Date()),
                            limit: rowsPerPage,
                          }}
                        />
                      </Styled.DownloadButtonWrapper>
                      <FormControl variant="filled">
                        <InputLabel focused={false}>{formatMessage({ id: AppMessages['events.refresh'] })}</InputLabel>
                        <Styled.RefreshSelect
                          variant="outlined"
                          value={refetchInterval}
                          onChange={(e) => setRefetchInterval(e.target.value as RefetchInterval)}
                        >
                          <MenuItem value={15000}>
                            {formatMessage({ id: AppMessages['events.refresh.fifteenSeconds'] })}
                          </MenuItem>
                          <MenuItem value={30000}>
                            {formatMessage({ id: AppMessages['events.refresh.thirtySeconds'] })}
                          </MenuItem>
                          <MenuItem value={60000}>
                            {formatMessage({ id: AppMessages['events.refresh.sixtySeconds'] })}
                          </MenuItem>
                        </Styled.RefreshSelect>
                      </FormControl>
                    </>
                  )}
                </Styled.PaginationWrapper>
              </>
            )}
          />
        </>
      )}
      renderDetails={() => (
        <DetailsDrawer isOpen={!!selectedEvent} header={formatMessage({ id: AppMessages['event.details.title'] })}>
          {selectedEvent && <EventDetailsContainer selectedEvent={selectedEvent} />}
        </DetailsDrawer>
      )}
    />
  );
};
